// tslint:disable:trailing-comma

import { paramsTunnel, ParamsTunnel } from './params-tunnel';
import {
  prefetchDataTunnel,
  PrefetchDataTunnel,
  GetPrefetchDataOptions,
} from './prefetch-data-tunnel';
import { defaultNeoConfig, NeoConfig } from './config';
import { showWebview as showWebviewBridge } from '../bridge';
import { stat, NeoBids, StatReportType } from '../stat';
import { setSessionStorage, hasSessionStorage } from '../util/storage';
import { setIsMock } from '../util/mock';
import { setDebugConfig, DebugConfig } from '../util/debug';

class Neo {
  // webview 显示标识 sessionStorage 键名
  private static webviewShownSessionKey = 'webview_shown';

  // 参数通道
  private paramsTunnel: ParamsTunnel = paramsTunnel;

  // 预请求数据通道
  private prefetchDataTunnel: PrefetchDataTunnel = prefetchDataTunnel;

  // 统计
  private stat = stat;

  // 配置
  private config!: NeoConfig;

  // 初始化 Promise
  private initPromise!: Promise<any>;

  private resolveInitPromise!: (value: any) => void;

  private rejectInitPromise!: (reason?: any) => void;

  constructor() {
    // 实例化初始化 Promise，引用 resolve/reject 回调
    this.initPromise = new Promise((resolve, reject) => {
      this.resolveInitPromise = resolve;
      this.rejectInitPromise = reject;
    });
  }

  /**
   * 初始化 Neo
   * @param {NeoConfig} config Neo 配置项
   */
  init<T>(config: NeoConfig): Promise<T> {
    // 合并配置
    this.config = { ...defaultNeoConfig, ...config };

    // 初始化统计模块
    this.stat.init({
      statBasicParams: this.config.statBasicParams,
      mapStatBasicParams: this.config.mapStatBasicParams,
    });

    // 设置是否开启 mock
    setIsMock(!!this.config.mock);

    // 设置 debug 配置
    setDebugConfig({
      level: this.config.debugLevel,
      namespace: this.config.debugNamespace,
    } as DebugConfig);

    // 参数通道获取数据
    this.paramsTunnel
      .fetch<T>(this.config.paramsValidator)
      .then((params: T) => {
        // 映射初始化参数至统计基础参数
        this.stat.mapBasicParams();

        // 预请求通道获取获取数据
        this.prefetchDataTunnel.fetch(this.config.prefetchKeyList);

        this.resolveInitPromise(params);
      })
      .catch((err) => {
        this.rejectInitPromise(err);
      });

    stat.sendToLX(NeoBids.NeoInitEnd, {}, StatReportType.VIEW);

    return this.initPromise;
  }

  /**
   * 获取预请求数据
   * @param {string} key 预请求数据 key，存在多个预请求数据的场景下，通过 key 值区分
   * @param {GetPrefetchDataOptions} options 请求选项
   * @returns {Promise} 预请求数据 Promise
   */
  getPrefetchData(
    key: string = 'default',
    options: GetPrefetchDataOptions = {}
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.initPromise
        .then(() => {
          this.prefetchDataTunnel
            .getPrefetchData(key, options)
            .then((data) => {
              resolve(data);
            })
            .catch((err) => {
              reject(err);
            });
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // TODO 桥调用失败需要加入重试机制
  /**
   * 显示 webview
   * @returns {Promise} 桥调用 Promise
   */
  showWebview(): Promise<any> {
    if (hasSessionStorage(Neo.webviewShownSessionKey, '1')) {
      return Promise.resolve();
    }
    setSessionStorage(Neo.webviewShownSessionKey, '1');

    return showWebviewBridge();
  }
}

const neo = new Neo();

export { neo };
export default neo;
